import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Mail, Inbox as InboxIcon, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { fmtTime } from "@/components/outreach/shared";
import WidgetSettings from "@/components/outreach/WidgetSettings";
import OAuthConnect from "@/components/outreach/OAuthConnect";
import CalendarSettings from "@/components/outreach/CalendarSettings";
import KnowledgeBase from "@/components/outreach/KnowledgeBase";
import SuppressionList from "@/components/outreach/SuppressionList";
import { BrandSettings, TeamSettings } from "@/components/outreach/TeamBrand";
import { ChannelsSettings, WebhooksSettings, ApiKeysSettings, AiDigestSettings } from "@/components/outreach/Integrations";
import WarmupControl from "@/components/outreach/WarmupControl";
import { SampleDataControls } from "@/components/Onboarding";
import NotificationSettings from "@/components/outreach/NotificationSettings";
import RebateSettings from "@/components/outreach/RebateSettings";
import PartnersSettings from "@/components/outreach/PartnersSettings";
import DocumentTemplates from "@/components/outreach/DocumentTemplates";
import SpeedToLeadSettings from "@/components/outreach/SpeedToLeadSettings";

const PROVIDER_BADGE = { gmail: "bg-rose-500/15 text-rose-500 border-rose-500/30", outlook: "bg-sky-500/15 text-sky-500 border-sky-500/30", smtp: "bg-muted text-muted-foreground border-border" };

export default function Settings() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(null);
  const load = async () => { const { data } = await api.get("/email-accounts"); setAccounts(data); };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (!id) return;
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    return () => clearTimeout(t);
  }, []);

  const remove = async (id) => {
    await api.delete(`/email-accounts/${id}`);
    toast.success("Removed");
    load();
  };
  const checkReplies = async (a) => {
    setChecking(a.id);
    try {
      const { data } = await api.post(`/email-accounts/${a.id}/check-replies`);
      toast.success(`Checked inbox: ${data.fetched} new, ${data.replies} matched replies`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setChecking(null); }
  };

  return (
    <Shell title="Settings">
      <div className="p-6 space-y-6 max-w-3xl">
        <section className="border border-border rounded-md bg-card p-5">
          <div className="text-sm font-medium">Workspace</div>
          <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Name</div>{user?.workspace_name}</div>
            <div><div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Role</div>{user?.role}</div>
            <div><div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Email</div><span className="mono">{user?.email}</span></div>
            <div><div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Workspace ID</div><span className="mono text-xs text-muted-foreground">{user?.workspace_id}</span></div>
          </div>
        </section>
        <div id="rebate"><RebateSettings /></div>
        <div id="partners"><PartnersSettings /></div>
        <div id="esign"><DocumentTemplates /></div>
        <SampleDataControls />
        <section id="email" className="border border-border rounded-md bg-card">
          <div className="p-5 border-b border-border flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Email accounts</div>
              <div className="text-xs text-muted-foreground">Inboxes used by sequences (rotated, daily-capped). Connect Gmail/Outlook in one click, or add SMTP (+IMAP for replies).</div>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="btn-new-email-account"><Plus className="w-4 h-4 mr-2" />Add SMTP account</Button></DialogTrigger>
              <NewAccountDialog onDone={() => { setOpen(false); load(); }} />
            </Dialog>
          </div>
          <OAuthConnect onConnected={load} />
          <div>
            {accounts.length === 0 && <div className="p-6 text-sm text-muted-foreground">No email accounts yet.</div>}
            {accounts.map((a) => (
              <div key={a.id} className="flex items-center gap-4 px-5 py-3 border-b border-border last:border-0" data-testid={`acct-${a.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium flex items-center gap-2"><Mail className="w-4 h-4 text-muted-foreground" />{a.name}
                    <span className={`text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded border ${PROVIDER_BADGE[a.provider] || PROVIDER_BADGE.smtp}`} data-testid={`acct-provider-${a.id}`}>{a.provider}</span>
                    {a.imap_enabled ? <span className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded border bg-blue-500/15 text-blue-500 border-blue-500/30">IMAP</span> : <span className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded border bg-muted text-muted-foreground border-border">send only</span>}
                  </div>
                  <div className="text-xs mono text-muted-foreground">{a.from_email}{a.provider === "smtp" ? ` · ${a.smtp_host}:${a.smtp_port}` : " · OAuth connected"}{a.oauth_error ? <span className="text-rose-500"> · {a.oauth_error}</span> : ""}</div>
                  {a.imap_enabled && <div className="text-[10px] mono text-muted-foreground mt-0.5">Replies checked {fmtTime(a.imap_last_check)}{a.imap_last_error ? <span className="text-rose-500"> · {a.imap_last_error}</span> : ""}</div>}
                </div>
                <div className="w-36 shrink-0">
                  <div className="flex justify-between text-[10px] mono text-muted-foreground"><span>{a.warmup ? `warmup d${a.warmup.day}` : "today"}</span><span data-testid={`acct-sent-${a.id}`}>{a.sent_today}/{a.effective_cap ?? a.daily_cap}</span></div>
                  <div className="h-1.5 bg-muted rounded-sm overflow-hidden mt-1"><div className={`h-full ${a.sent_today >= (a.effective_cap ?? a.daily_cap) ? "bg-rose-500" : "bg-primary"}`} style={{ width: `${Math.min(100, (a.sent_today / Math.max(1, a.effective_cap ?? a.daily_cap)) * 100)}%` }} /></div>
                </div>
                <WarmupControl account={a} onChanged={load} />
                <Button variant="ghost" size="icon" title="Check replies now" disabled={!a.imap_enabled || checking === a.id} onClick={() => checkReplies(a)} data-testid={`check-replies-${a.id}`}><RefreshCw className={`w-4 h-4 ${checking === a.id ? "animate-spin" : ""}`} /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(a.id)} data-testid={`del-acct-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </section>
        <BrandSettings />
        <TeamSettings />
        <div id="channels"><ChannelsSettings /></div>
        <div id="notifications"><NotificationSettings /></div>
        <div id="speed"><SpeedToLeadSettings /></div>
        <AiDigestSettings />
        <WebhooksSettings />
        <ApiKeysSettings />
        <CalendarSettings />
        <div id="widget"><WidgetSettings /></div>
        <KnowledgeBase />
        <SuppressionList />
        <section className="border border-border rounded-md bg-card p-5">
          <div className="text-sm font-medium flex items-center gap-2"><InboxIcon className="w-4 h-4 text-muted-foreground" />Inbound webhook</div>
          <div className="text-xs text-muted-foreground mt-1">No IMAP? Forward replies from your email provider (Mailgun/SendGrid inbound parse, Zapier) to <span className="mono text-foreground">POST /api/inbound/email</span> with your bearer token and <span className="mono">{"{ from_email, subject, body, in_reply_to? }"}</span>. Matched replies stop sequences and advance deals automatically.</div>
        </section>
      </div>
    </Shell>
  );
}

function NewAccountDialog({ onDone }) {
  const [form, setForm] = useState({ name: "", from_email: "", smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "", daily_cap: 50, imap_host: "", imap_port: 993, imap_user: "", imap_password: "" });
  const [imap, setImap] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, smtp_port: Number(form.smtp_port), daily_cap: Number(form.daily_cap), imap_port: Number(form.imap_port) };
      if (!imap) { payload.imap_host = ""; payload.imap_user = ""; payload.imap_password = ""; }
      await api.post("/email-accounts", payload);
      toast.success("Account added");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  return (
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Add email account</DialogTitle><DialogDescription>SMTP for sending; optional IMAP for reply detection.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Name</Label><Input data-testid="acct-name" value={form.name} onChange={upd("name")} required /></div>
          <div className="space-y-1.5"><Label>From email</Label><Input data-testid="acct-from" type="email" value={form.from_email} onChange={upd("from_email")} required /></div>
        </div>
        <div className="grid grid-cols-[1fr_6rem] gap-3">
          <div className="space-y-1.5"><Label>SMTP host</Label><Input data-testid="acct-host" value={form.smtp_host} onChange={upd("smtp_host")} placeholder="smtp.gmail.com" required /></div>
          <div className="space-y-1.5"><Label>Port</Label><Input data-testid="acct-port" type="number" value={form.smtp_port} onChange={upd("smtp_port")} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>SMTP user</Label><Input data-testid="acct-user" value={form.smtp_user} onChange={upd("smtp_user")} /></div>
          <div className="space-y-1.5"><Label>SMTP password</Label><Input data-testid="acct-password" type="password" value={form.smtp_password} onChange={upd("smtp_password")} /></div>
        </div>
        <div className="space-y-1.5"><Label>Daily send cap</Label><Input data-testid="acct-cap" type="number" value={form.daily_cap} onChange={upd("daily_cap")} /></div>
        <div className="border border-border rounded-md p-3 space-y-3 bg-muted/20">
          <label className="flex items-center justify-between text-sm"><span className="font-medium">Reply detection (IMAP)</span><Switch data-testid="acct-imap-toggle" checked={imap} onCheckedChange={setImap} /></label>
          {imap && (
            <>
              <div className="grid grid-cols-[1fr_6rem] gap-3">
                <div className="space-y-1.5"><Label>IMAP host</Label><Input data-testid="acct-imap-host" value={form.imap_host} onChange={upd("imap_host")} placeholder="imap.gmail.com" required={imap} /></div>
                <div className="space-y-1.5"><Label>Port</Label><Input data-testid="acct-imap-port" type="number" value={form.imap_port} onChange={upd("imap_port")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>IMAP user</Label><Input data-testid="acct-imap-user" value={form.imap_user} onChange={upd("imap_user")} placeholder="defaults to SMTP user" /></div>
                <div className="space-y-1.5"><Label>IMAP password</Label><Input data-testid="acct-imap-password" type="password" value={form.imap_password} onChange={upd("imap_password")} placeholder="defaults to SMTP password" /></div>
              </div>
            </>
          )}
        </div>
        <Button type="submit" data-testid="acct-submit" className="w-full">Save account</Button>
      </form>
    </DialogContent>
  );
}
